<template>
  <div class="ai-terms-acceptance">
    <div class="terms-header">
      <v-icon size="52" color="primary">mdi-robot-outline</v-icon>
      <div>
        <h3 class="terms-title">{{ t('aiAssistant.terms.title') }}</h3>
        <p class="terms-subtitle">{{ t('aiAssistant.terms.subtitle') }}</p>
      </div>
    </div>

    <v-divider />

    <div class="terms-conditions">
      <div v-for="condition in conditions" :key="condition.key" class="condition-item">
        <v-icon :color="condition.color" size="24" class="condition-icon">{{
          condition.icon
        }}</v-icon>
        <div class="condition-text">
          <span class="condition-title">{{
            t(`aiAssistant.terms.conditions.${condition.key}.title`)
          }}</span>
          <span class="condition-body">{{
            t(`aiAssistant.terms.conditions.${condition.key}.body`)
          }}</span>
        </div>
      </div>
    </div>

    <v-divider />

    <div class="terms-footer">
      <p class="terms-consent-label">{{ t('aiAssistant.terms.consentLabel') }}</p>
      <v-btn
        color="primary"
        :loading="isAccepting"
        :disabled="isAccepting"
        block
        data-id-e2e="aiTermsAcceptBtn"
        @click="handleAccept"
      >
        {{ t('aiAssistant.terms.acceptButton') }}
      </v-btn>
      <p v-if="error" class="terms-error">{{ error }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useAiStore } from '@/stores/ai';
import { useInternationalization } from '@/composables/use-internationalization';

const aiStore = useAiStore();
const { t } = useInternationalization();

const isAccepting = ref(false);
const error = ref(null);

const conditions = [
  { key: 'accuracy', icon: 'mdi-alert-circle-outline', color: 'warning' },
  { key: 'judgement', icon: 'mdi-account-check-outline', color: 'primary' },
  { key: 'logging', icon: 'mdi-eye-outline', color: 'secondary' },
  { key: 'acceptable', icon: 'mdi-check-circle-outline', color: 'success' },
  { key: 'dataRetention', icon: 'mdi-shield-lock-outline', color: 'info' },
  { key: 'dataAccess', icon: 'mdi-laptop-off', color: 'secondary' },
];

async function handleAccept() {
  isAccepting.value = true;
  error.value = null;

  try {
    await aiStore.acceptAiTerms();
  } catch (err) {
    error.value = t('aiAssistant.terms.acceptError');
    console.error('Failed to accept AI terms:', err);
  } finally {
    isAccepting.value = false;
  }
}
</script>

<style lang="scss" scoped>
.ai-terms-acceptance {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.terms-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 20px 16px;
  flex-shrink: 0;
}

.terms-title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  line-height: 1.3;
}

.terms-subtitle {
  margin: 0;
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.65);
  line-height: 1.4;
}

.terms-conditions {
  flex: 1;
  overflow-y: auto;
  padding: 8px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.condition-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));

  &:last-child {
    border-bottom: none;
  }
}

.condition-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.condition-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.condition-title {
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  line-height: 1.4;
}

.condition-body {
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.75);
  line-height: 1.5;
}

.terms-footer {
  padding: 16px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}

.terms-consent-label {
  margin: 0;
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  line-height: 1.4;
  text-align: center;
}

.terms-error {
  margin: 0;
  font-size: 14px;
  color: rgb(var(--v-theme-error));
  text-align: center;
}
</style>
